import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import { Box } from "@mui/material";
import { type FC, type ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useGlobalManualLocationOrder } from "../../../../contexts/Staffing/GlobalManualLocationOrderContext";
import { toTestIdSlug } from "../../../../utils/strings";
import { SheetActionRow } from "../../../UIToolkit/Clickable/SheetActionRow";
import { SheetStepSectionLabel } from "../../../UIToolkit/Popups/SheetStepSectionLabel";
import { getStaffingStatusStyle } from "../../WeeklyGapView/CountBadge";
import { formatStaffCount, type GapsLocationEntry } from "../gapsDayModel";
import { orderLocationsEmptiestFirst } from "../gapsGroupSheets";
import { useGapsCountsHidden } from "../useGapsCountsHidden";

type Props = {
	/** The group's own units, each with its filled/required for the window. */
	units: readonly GapsLocationEntry[];
	/** The department's other units, by name (`useGapsOtherUnits`) — listed after the group's, without counts (the window does not run there). */
	otherUnits: readonly string[];
	/** The person's current unit, `""` when unassigned. Marked as the checked choice. */
	currentLocation: string;
	/** The unit whose save is in flight — its row shows progress and the others wait. */
	savingLocation: string | null;
	onPick: (locationName: string) => void;
	/** Per-row test ids are `{rowTestIdPrefix}-{location as a kebab-case slug}` ("2ND FLOOR - 2A" → `2nd-floor-2a`). */
	rowTestIdPrefix: string;
};

/**
 * Units to place someone in, emptiest first, each with its own `filled/required` (P3-5, P3-6).
 *
 * The order is `orderLocationsEmptiestFirst` — the most people still missing first, ties in the department's own
 * location order — so the unit that most needs the person is the first thing under the thumb. Counts print in the
 * desktop weekly gap view's inks (`getStaffingStatusStyle`: red from two short, amber when over), because it is the
 * same cell. A unit no config runs in during this window is still offered, quietly, with the reason it reads 0/0.
 */
export const GapsUnitList: FC<Props> = ({ units, otherUnits, currentLocation, savingLocation, onPick, rowTestIdPrefix }) => {
	const { t } = useTranslation();
	const { areCountsHidden } = useGapsCountsHidden();
	const { customStringOrderMap } = useGlobalManualLocationOrder();
	const ordered = useMemo(() => orderLocationsEmptiestFirst(units, customStringOrderMap), [units, customStringOrderMap]);
	const isSaving = savingLocation !== null;
	// The two headings only split two lists: a window whose config names no units lists the department's, unheaded.
	const isSplit = ordered.length > 0 && otherUnits.length > 0;

	const row = (locationName: string, trailing: ReactNode, description?: string) => (
		<SheetActionRow
			key={locationName}
			kind="radio"
			checked={locationName === currentLocation}
			icon={<PlaceOutlinedIcon />}
			label={locationName}
			description={description}
			trailing={trailing}
			loading={savingLocation === locationName}
			disabled={isSaving && savingLocation !== locationName}
			onClick={() => onPick(locationName)}
			dataTestId={`${rowTestIdPrefix}-${toTestIdSlug(locationName)}`}
		/>
	);

	return (
		<Box role="radiogroup" aria-label={t("gaps.assignUnit.units")}>
			{isSplit && <SheetStepSectionLabel>{t("gaps.staffSheet.onThisShift")}</SheetStepSectionLabel>}
			{ordered.map((unit) => {
				const style = getStaffingStatusStyle(unit.filled, unit.required);
				return row(
					unit.locationName,
					areCountsHidden ? undefined : (
						<Box component="span" sx={{ color: style.isFlagged ? style.color : undefined, fontWeight: style.isFlagged ? 700 : 500 }}>
							{`${formatStaffCount(unit.filled)}/${formatStaffCount(unit.required)}`}
						</Box>
					),
					unit.isBlocked ? t("gaps.assignUnit.notRunning") : undefined,
				);
			})}
			{isSplit && <SheetStepSectionLabel>{t("gaps.staffSheet.otherUnits")}</SheetStepSectionLabel>}
			{otherUnits.map((locationName) => row(locationName, undefined))}
		</Box>
	);
};
